import React, { useState, useEffect } from 'react';
import { Check } from 'lucide-react';
import SideBar from '../components/SideBar';
import { GET_PLANS, make_payment } from '../route';
import authApi from '../auth.api';
import { toast } from 'react-toastify';

const PlanCard = ({ name, price, features, recommended, onSelect, _id }) => (
  <div
    className={`bg-white rounded-lg shadow-md p-8 flex flex-col justify-between min-h-[350px] ${
      recommended ? 'border-2 border-indigo-500' : ''
    }`}
  >
    {recommended && (
      <div className='bg-indigo-500 text-white text-xs font-bold uppercase py-1 px-2 rounded-full self-start mb-2'>
        Recommended
      </div>
    )}
    <h3 className='text-xl font-semibold mb-2'>{name}</h3>
    <p className='text-3xl font-bold mb-4'>
      ${price}
      <span className='text-sm font-normal'>/month</span>
    </p>
    <ul className='mb-6 flex-grow'>
      {features.map((feature, index) => (
        <li key={index} className='flex items-center mb-2'>
          <Check className='h-5 w-5 text-green-500 mr-2' />
          <span>{feature.value}</span>
        </li>
      ))}
    </ul>
    <button
      onClick={() => onSelect({ name, price, features, _id })}
      className={`w-full py-3 px-4 rounded-md font-medium bg-indigo-600 text-white hover:bg-indigo-700`}
    >
      Select Plan
    </button>
  </div>
);

const UpgradePlan = () => {
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [blackFridayCode, setBlackFridayCode] = useState('');
  const [discountedPrice, setDiscountedPrice] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await authApi.get(GET_PLANS);
      const allPlans = response.data.plans;
      // Remove the lowest plan (free plan)
      const paidPlans = allPlans.filter((plan) => plan.price > 0);

      if (paidPlans.length > 0) {
        // Sort plans by price (high to low)
        paidPlans.sort((a, b) => b.price - a.price);

        // Check if there are at least 2 plans
        if (paidPlans.length >= 2) {
          // Reset any existing recommended flags
          paidPlans.forEach(plan => plan.recommended = false);

          // Make the second most expensive plan (index 1) the recommended plan
          paidPlans[1].recommended = true;
        } else {
          // If there's only one plan, make it recommended
          paidPlans[0].recommended = true;
        }

        setPlans(paidPlans);
      }    } catch (error) {
      toast.error('Failed to fetch plans');
    }
  };

  const handlePlanSelection = (plan) => {
    setSelectedPlan(plan);
    setShowConfirmation(true);
    // Reset discount when a new plan is selected
    setBlackFridayCode('');
    setDiscountedPrice(null);
  };

  const applyBlackFridayCode = async () => {
    if (!blackFridayCode) {
      toast.error('Please enter a code');
      return;
    }

    setLoading(true);
    const code = "ART10"
    if (blackFridayCode.toLowerCase() === code.toLowerCase()) {
      const discountPercentage = 10;
      const originalPrice = selectedPlan.price;
      const newPrice = originalPrice * (1 - discountPercentage / 100);
      setDiscountedPrice(newPrice.toFixed(2));
      setLoading(false);
      return toast.success(`Code applied! ${discountPercentage}% off.`);
    }
    toast.error('Invalid or expired code');
    setLoading(false);
  };

  const confirmSelection = async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      const result = await authApi.post(make_payment, {
        plan: selectedPlan._id,
        blackFridayCode: blackFridayCode || null,
      });
      if (!result.status) {
        return toast.error('unable to generate payment link');
      }
      const { url } = result.data;
      return window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Payment failed');
    } finally {
      setLoading(false);
      setShowConfirmation(false);
    }
  };

  const cancelSelection = () => {
    setShowConfirmation(false);
    setBlackFridayCode('');
    setDiscountedPrice(null);
  };

  return (
    <div className='flex h-screen bg-gray-50'>
      <SideBar />
      <div className='flex-1 overflow-x-hidden overflow-y-auto px-6 py-8 md:p-8'>
        <div className='max-w-7xl mx-auto'>
          {/* Header Section */}
          <div className='text-center max-w-2xl mx-auto mb-12'>
            <h2 className='text-2xl md:text-3xl font-bold text-gray-800 mb-4'>
              Upgrade Your Plan
            </h2>
            <p className='text-gray-600'>
              Choose the plan that best fits your needs and take your content
              creation to the next level!
            </p>
            {errorMessage && (
              <div className='mt-4 p-3 bg-red-50 text-red-500 rounded-lg'>
                {errorMessage}
              </div>
            )}
          </div>

          {/* Plans Grid */}
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6'>
            {plans.map((plan, index) => (
              <PlanCard key={index} {...plan} onSelect={handlePlanSelection} />
            ))}
          </div>
        </div>

        {/* Confirmation Modal */}
        {showConfirmation && (
          <div className='fixed inset-0 bg-black/50 flex items-center justify-center p-4 backdrop-blur-sm'>
            <div className='bg-white rounded-lg shadow-xl w-full max-w-md'>
              <div className='p-6'>
                <h3 className='text-xl font-bold text-gray-800 mb-4'>
                  Confirm Your Selection
                </h3>
                <p className='text-gray-600 mb-4'>
                  You have selected the{' '}
                  <span className='font-semibold text-gray-800'>
                    {selectedPlan.name}
                  </span>{' '}
                  plan for{' '}
                  <span className='font-semibold text-gray-800'>
                    ${discountedPrice || selectedPlan.price}
                  </span>
                  /month.
                </p>

                {/* Holiday Code Input */}
                <div className='mb-4'>
                  <label
                    htmlFor='holidayCode'
                    className='block text-sm font-medium text-gray-700 mb-2'
                  >
                    Promo Code (Optional)
                  </label>
                  <div className='flex'>
                    <input
                      type='text'
                      id='holidayCode'
                      value={blackFridayCode}
                      onChange={(e) => setBlackFridayCode(e.target.value)}
                      placeholder='Enter Promo code'
                      className='flex-grow mr-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500'
                    />
                    <button
                      onClick={applyBlackFridayCode}
                      disabled={loading}
                      className='px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50'
                    >
                      Apply
                    </button>
                  </div>
                  {discountedPrice && (
                    <p className='text-green-600 text-sm mt-2'>
                      Discount applied! Original price: ${selectedPlan.price},
                      New price: ${discountedPrice}
                    </p>
                  )}
                </div>

                {errorMessage && (
                  <div className='mb-4 p-3 bg-red-50 text-red-500 rounded-lg text-sm'>
                    {errorMessage}
                  </div>
                )}

                <div className='flex justify-end items-center gap-3 mt-6'>
                  <button
                    onClick={cancelSelection}
                    className='px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200'
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmSelection}
                    disabled={loading}
                    className={`
                                            px-4 py-2 text-white bg-indigo-600 rounded-lg
                                            ${
                                              loading
                                                ? 'opacity-75 cursor-not-allowed'
                                                : 'hover:bg-indigo-700'
                                            }
                                            transition-colors duration-200
                                        `}
                  >
                    {loading ? 'Processing...' : 'Confirm'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpgradePlan;
